import { merge } from 'lodash-es';
import React, { type ComponentProps } from 'react';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { AlignStackPresetProp } from '../../utilities/AlignStack/AlignStack';
import type { GutterContext, GutterProp } from '../../utilities/shared/Gutter';
import type { OverflowProps } from '../../utilities/shared/Overflow';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { AccessLevelProp } from '../../utilities/useAccessLevelContext';
import type { BetaProp } from '../../utilities/useBeta';
import type { InternalProp } from '../../utilities/useInternal';
import type { BadgeProps } from '../Badge/Badge';
import type { SectionFadeProp } from './utils';
import { type DividerAppearance, Divider } from '../../assets/Divider/Divider';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import { backgrounds } from '../../common/backgrounds';
import { shadows } from '../../common/shadows';
import { sizing } from '../../common/sizing';
import { transitions } from '../../common/transitions';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { Tabs } from '../../controls/Tabs/Tabs';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Small } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { fadeMaskCSS } from '../../utilities/shared/FadeMask';
import { getGutterTokens, gutterCSS } from '../../utilities/shared/Gutter';
import { hasOverflow, overflowCSS } from '../../utilities/shared/Overflow';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { HStack } from '../../utilities/Stack/HStack';
import {
  AccessLevel,
  ClearAccessLevelContext,
  isAccessLevelVisible,
  UserAccessLevelContextProvider,
} from '../../utilities/useAccessLevelContext';
import { useAccessLevelOrInternal } from '../../utilities/useInternal';
import { Badge } from '../Badge/Badge';
import { BadgeGroup } from '../Badge/BadgeGroup';
import { LabelTooltip } from '../Tooltip/Tooltip';
import { SectionProvider, useSectionContext } from './utils';

const sectionDefaults = {
  relation: 'standalone',
  size: 'medium',
};

const SectionHeaderIcon = styled(DynamicIcon, {
  color: colors.bodyNeutralLight,

  [darkThemeSelector]: {
    color: colors.bodyNeutralDark,
  },

  variants: {
    hasSize: {
      'x-small': {
        width: '$12',
        height: '$12',
      },
      small: {
        width: '$14',
        height: '$14',
      },
      medium: {
        width: '$16',
        height: '$16',
      },
    },
  },
});
SectionHeaderIcon.displayName = 'SectionHeaderIcon';

export const SectionHeaderHeading = styled(Text, {
  display: 'flex',
  width: '100%',
  minWidth: 0,
});
SectionHeaderHeading.displayName = 'SectionHeaderHeading';

const SectionHeaderDescription = styled(Small, {
  display: 'flex',
  width: '100%',
  minWidth: 0,
});
SectionHeaderDescription.displayName = 'SectionHeaderDescription';

const SectionHeaderCopy = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
});
SectionHeaderCopy.displayName = 'SectionHeaderCopy';

const SectionHeaderActions = styled(ControlGroup);
SectionHeaderActions.displayName = 'SectionHeaderActions';

const SectionHeaderTabs = styled(Tabs, {
  minHeight: 'auto',
});
SectionHeaderTabs.displayName = 'SectionHeaderTabs';

const SectionHeaderStart = styled('div', {
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
});
SectionHeaderStart.displayName = 'SectionHeaderStart';

const SectionHeaderEnd = styled('div', {
  minWidth: 0,
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
});
SectionHeaderEnd.displayName = 'SectionHeaderEnd';

const SectionHeaderContainer = styled('div', {
  minWidth: 0,
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',

  variants: {
    collapsed: {
      true: {},
      false: {},
    },
    size: {
      'x-small': {
        gap: '$6',
        minHeight: '$20',
      },
      small: {
        minHeight: '$20',
        gap: '$6',
      },
      medium: {
        minHeight: '$24',
        gap: '$8',
      },
    },
    relation: {
      stacked: {},
      standalone: {
        '&:not(:last-child)': {
          strokeBottom: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeBottom: colors.strokeNeutralDark,
          },
        },
      },
      'full-width': {},
    },
  },

  compoundVariants: [
    {
      collapsed: true,
      relation: 'standalone',
      css: {
        '&:not(:last-child)': {
          strokeBottom: colors.transparent,

          [darkThemeSelector]: {
            strokeBottom: colors.transparent,
          },
        },
      },
    },
    {
      size: 'x-small',
      relation: 'stacked',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      size: 'small',
      relation: 'stacked',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      size: 'medium',
      relation: 'stacked',
      css: {
        padding: `$6 ${sizing.sides}`,
      },
    },
    {
      size: 'x-small',
      relation: 'standalone',
      css: {
        padding: `$6 ${sizing.contentSides}`,
      },
    },
    {
      size: 'small',
      relation: 'standalone',
      css: {
        padding: `$6 ${sizing.contentSides}`,
      },
    },
    {
      size: 'medium',
      relation: 'standalone',
      css: {
        padding: `$8 ${sizing.contentSides}`,
      },
    },
    {
      size: 'x-small',
      relation: 'full-width',
      css: {
        padding: '$4 0',
      },
    },
    {
      size: 'small',
      relation: 'full-width',
      css: {
        padding: '$4 0',
      },
    },
    {
      size: 'medium',
      relation: 'full-width',
      css: {
        padding: '$6 0',
      },
    },
  ],
});
SectionHeaderContainer.displayName = 'SectionHeaderContainer';

export type SectionHeaderProps = {
  /**
   * Access level for the section
   */
  accessLevel?: AccessLevelProp;
  /**
   * Provide any actions.
   */
  actions?: React.ReactNode;
  /**
   * Provide any badges.
   */
  badges?: Omit<BadgeProps, 'size'>[];
  /**
   * Boolean to signify the section as beta.
   */
  beta?: BetaProp;
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * Provide a count for your pane.
   */
  count?: number;
  /**
   * Provide a description for your pane.
   */
  description?: React.ReactNode;
  /**
   * Provide a heading for your pane.
   */
  heading?: React.ReactNode;
  /**
   * Provide an icon for your pane.
   */
  icon?: DynamicIconName;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * Boolean to signify the section as internal.
   */
  internal?: InternalProp;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation | 'full-width';
  /**
   * The size of the section header.
   */
  size?: SectionPropSize;
  /**
   * Provide any tabs you want to display.
   */
  tabs?: React.ReactNode;
  /**
   * Provide a tooltip for the heading.
   */
  tooltip?: React.ReactNode;
  style?: React.CSSProperties;
};

export function SectionHeader({
  accessLevel: accessLevelProp,
  actions,
  badges,
  beta,
  count,
  collapsed: collapsedProp,
  description,
  heading,
  icon,
  id,
  internal,
  relation: relationProp,
  size: sizeProp,
  tabs,
  tooltip,
  ...remaining
}: SectionHeaderProps) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });
  const sectionContext = useSectionContext();
  const collapsed = collapsedProp ?? sectionContext.collapsed;
  const relation = relationProp ?? sectionContext.relation;
  const size = sizeProp ?? sectionContext.size;
  const effectiveRelation = relation ?? sectionDefaults.relation;
  const effectiveSize = size ?? sectionDefaults.size;
  const hasLockup = icon || heading || (accessLevel && accessLevel !== AccessLevel.Public);

  let headingPreset: AlignStackPresetProp = 'body';
  if (effectiveSize === 'x-small') {
    headingPreset = 'small';
  }

  const headingRender = (
    <SectionHeaderHeading context="heading" variant="neutral" weight="bold">
      {heading}
    </SectionHeaderHeading>
  );

  const hasEnd = count || accessLevel || badges;

  const toggleCollapsed = sectionContext.collapsible
    ? () => sectionContext.setCollapsed(!sectionContext.collapsed)
    : undefined;

  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      <SectionHeaderContainer
        collapsed={collapsed}
        relation={effectiveRelation}
        size={effectiveSize}
        {...remaining}
      >
        <SectionHeaderStart>
          {hasLockup && (
            <AlignStack
              onClick={toggleCollapsed}
              tabIndex={sectionContext.collapsible ? -1 : undefined}
              gap={effectiveSize === 'medium' ? 6 : 4}
              preset={headingPreset}
              start={
                (sectionContext.collapsible || icon) && (
                  <HStack align="center" spacing={effectiveSize === 'medium' ? 6 : 4} wrap="nowrap">
                    {sectionContext.collapsible && (
                      <UtilityControl
                        aria-expanded={!collapsed}
                        icon={collapsed ? 'chevron-right' : 'chevron-down'}
                        label={collapsed ? 'Expand' : 'Collapse'}
                        variant="neutral"
                      />
                    )}
                    {icon && <SectionHeaderIcon icon={icon} hasSize={sectionContext.size} />}
                  </HStack>
                )
              }
              end={
                hasEnd && (
                  <>
                    {count != null && (
                      <Badge
                        ends="card"
                        size={effectiveSize === 'medium' ? 'small' : 'x-small'}
                        variant="neutral"
                      >
                        {count}
                      </Badge>
                    )}
                    {badges && (
                      <BadgeGroup
                        relation="separate"
                        size={effectiveSize === 'medium' ? 'small' : 'x-small'}
                      >
                        {badges.map((badge) => (
                          <Badge key={badge.id} {...badge} />
                        ))}
                      </BadgeGroup>
                    )}
                    {accessLevel && isAccessLevelVisible(accessLevel) && (
                      <FeatureBadge size="x-small" type={accessLevel} />
                    )}
                  </>
                )
              }
            >
              {(heading || description) && (
                <SectionHeaderCopy>
                  {heading &&
                    (tooltip ? (
                      <LabelTooltip contents={tooltip} preset={headingPreset} width="full">
                        {headingRender}
                      </LabelTooltip>
                    ) : (
                      headingRender
                    ))}
                  {description && (
                    <SectionHeaderDescription>{description}</SectionHeaderDescription>
                  )}
                </SectionHeaderCopy>
              )}
            </AlignStack>
          )}
          {tabs && <SectionHeaderTabs>{tabs}</SectionHeaderTabs>}
        </SectionHeaderStart>
        <SectionHeaderEnd>
          {actions && (
            <SectionHeaderActions
              relation="separate"
              size={effectiveSize === 'x-small' ? 'x-small' : 'small'}
            >
              {actions}
            </SectionHeaderActions>
          )}
        </SectionHeaderEnd>
      </SectionHeaderContainer>
    </UserAccessLevelContextProvider>
  );
}

const SectionContentInset = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  minHeight: 'min-content',
  boxSizing: 'border-box',
});
SectionContentInset.displayName = 'SectionContentInset';

const SectionContentContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  variants: {
    background: {
      default: {},
      checkered: {
        background: backgrounds.checkeredLight,

        [darkThemeSelector]: {
          background: backgrounds.checkeredDark,
        },
      },
      dotted: {
        background: backgrounds.dottedLight,

        [darkThemeSelector]: {
          background: backgrounds.dottedDark,
        },
      },
    },
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
    hasScroll: {
      true: {},
      false: {},
    },
    relation: {
      stacked: {},
      standalone: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      hasScroll: true,
      css: {
        position: 'relative',
        width: '100%',
        minWidth: 0,
        height: 'fit-content',

        '&[class]:first-child, &[class]:last-child': {
          marginTop: 0,
          marginBottom: 0,
        },
      },
    },
  ],
});
SectionContentContainer.displayName = 'SectionContentContainer';

/**
 * A mask clips everything its element paints, and this ring is an outer shadow drawn beyond the
 * border box — so a faded section erases it rather than dimming it, hence the `maskImage` reset.
 * Bare `&:focus-visible`, not `selectors.focus`: that also matches `:has(:focus-visible)`, which
 * would light the ring for any control inside.
 */
const scrollFocusCSS = {
  transition: transitions.focusRing,

  '&:focus-visible': {
    outline: 'none',
    boxShadow: shadows.focusRingLight,
    maskImage: 'none',

    [darkThemeSelector]: {
      boxShadow: shadows.focusRingDark,
    },
  },
};

export type SectionContentProps = {
  /**
   * Set the background of the section.
   */
  background?: 'default' | 'checkered' | 'dotted';
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section.
   */
  size?: SectionPropSize;
  /**
   * The spacing of the section.
   */
  spacing?: SpacingScale;
} & GutterProp &
  OverflowProps &
  ComponentProps<typeof SectionContentContainer>;

export function SectionContent({
  background,
  children,
  collapsed: collapsedProp,
  gutter = { edge: 'none' },
  id,
  relation: relationProp,
  size: sizeProp,
  spacing = 0,
  overflow,
  ...remaining
}: SectionContentProps) {
  const sectionContext = useSectionContext();
  const collapsed = collapsedProp ?? sectionContext.collapsed;
  const relation = relationProp ?? sectionContext.relation;
  const size = sizeProp ?? sectionContext.size;
  const effectiveRelation = relation ?? sectionDefaults.relation;
  const effectiveSize = size ?? sectionDefaults.size;
  let effectiveGutterContext: GutterContext = 'content';
  if (effectiveRelation === 'stacked') {
    effectiveGutterContext = 'section';
  }
  if (effectiveSize === 'small' || effectiveSize === 'x-small') {
    effectiveGutterContext = 'content';
  }

  const { ends: fadeSizeToken } = getGutterTokens(effectiveGutterContext, gutter.edge);

  const scrollableOverflow = hasOverflow(overflow);
  const fadeEdges = sectionContext.fade;
  const showFade = fadeEdges !== 'none' && scrollableOverflow;

  const gutterAndSpacingCss = merge(
    gutterCSS({ context: effectiveGutterContext, ...gutter }),
    spacingCSS(spacing),
  );

  const scrollSurfaceCss = merge(
    overflowCSS(overflow),
    showFade ? fadeMaskCSS(fadeSizeToken, fadeEdges) : undefined,
    scrollFocusCSS,
    remaining.css,
  );

  // `{}` is load-bearing: `merge` writes into its first argument, and the scrolling branch below
  // hands `gutterAndSpacingCss` to the inset. Without it the inset also gets `overflow` and the
  // caller's `css`, making it a second, competing scroll container.
  const flatCss = merge(
    {},
    gutterAndSpacingCss,
    overflowCSS(overflow),
    showFade ? fadeMaskCSS(fadeSizeToken, fadeEdges) : undefined,
    remaining.css,
  );

  if (scrollableOverflow) {
    return (
      <SectionContentContainer
        background={background}
        collapsed={collapsed}
        relation={effectiveRelation}
        size={effectiveSize}
        hasScroll
        // Without this the box scrolls but never takes focus, so the arrow keys have nothing to
        // drive. Chrome 127+ does this natively; Firefox and Safari don't. Fires even when the
        // content happens to fit, since knowing otherwise means measuring the DOM.
        tabIndex={0}
        {...remaining}
        css={scrollSurfaceCss}
      >
        <SectionContentInset css={gutterAndSpacingCss}>
          <ClearAccessLevelContext>{children}</ClearAccessLevelContext>
        </SectionContentInset>
      </SectionContentContainer>
    );
  }

  return (
    <SectionContentContainer
      background={background}
      collapsed={collapsed}
      relation={effectiveRelation}
      size={effectiveSize}
      hasScroll={false}
      {...remaining}
      css={flatCss}
    >
      <ClearAccessLevelContext>{children}</ClearAccessLevelContext>
    </SectionContentContainer>
  );
}

const SectionFooterHelper = styled(Small);
SectionFooterHelper.displayName = 'SectionFooterHelper';

const SectionFooterEnds = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$8',
});
SectionFooterEnds.displayName = 'SectionFooterEnds';

const SectionFooterStart = styled(SectionFooterEnds);
SectionFooterStart.displayName = 'SectionFooterStart';

const SectionFooterEnd = styled(SectionFooterEnds);
SectionFooterEnd.displayName = 'SectionFooterEnd';

const SectionFooterContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  gap: '$12',
  justifyContent: 'space-between',

  variants: {
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
    relation: {
      stacked: {},
      standalone: {
        strokeTop: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          strokeTop: colors.strokeNeutralDark,
        },

        '&:first-child': {
          strokeTop: colors.transparent,

          [darkThemeSelector]: {
            strokeTop: colors.transparent,
          },
        },
      },
    },
  },

  compoundVariants: [
    {
      size: 'x-small',
      relation: 'stacked',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      size: 'small',
      relation: 'stacked',
      css: {
        padding: `$4 ${sizing.contentSides}`,
      },
    },
    {
      size: 'medium',
      relation: 'stacked',
      css: {
        padding: `$6 ${sizing.sides}`,
      },
    },
    {
      size: 'x-small',
      relation: 'standalone',
      css: {
        padding: `$8 ${sizing.contentSides}`,
      },
    },
    {
      size: 'small',
      relation: 'standalone',
      css: {
        padding: `$8 ${sizing.contentSides}`,
      },
    },
    {
      size: 'medium',
      relation: 'standalone',
      css: {
        padding: `$12 ${sizing.contentSides}`,
      },
    },
  ],
});
SectionFooterContainer.displayName = 'SectionFooterContainer';

export type SectionFooterProps = {
  /**
   * Boolean to collapse the section.
   */
  collapsed?: boolean;
  /**
   * Provide any actions for the right-side.
   */
  endActions?: React.ReactNode;
  /**
   * Any helper text that should be shown.
   */
  helper?: React.ReactNode;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * The relation of the section.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section.
   */
  size?: SectionPropSize;
  /**
   * Provide any actions for the left-side.
   */
  startActions?: React.ReactNode;
};

export function SectionFooter({
  collapsed: collapsedProp,
  endActions,
  helper,
  id,
  relation: relationProp,
  size: sizeProp,
  startActions,
  ...remaining
}: SectionFooterProps) {
  const sectionContext = useSectionContext();
  const collapsed = collapsedProp ?? sectionContext.collapsed;
  const relation = relationProp ?? sectionContext.relation;
  const size = sizeProp ?? sectionContext.size;
  const effectiveRelation = relation ?? sectionDefaults.relation;
  const effectiveSize = size ?? sectionDefaults.size;
  return (
    <SectionFooterContainer
      collapsed={collapsed}
      relation={effectiveRelation}
      size={effectiveSize}
      {...remaining}
    >
      <SectionFooterStart>
        {startActions && (
          <ControlGroup size="small" relation="separate">
            {startActions}
          </ControlGroup>
        )}
        {helper && <SectionFooterHelper>{helper}</SectionFooterHelper>}
      </SectionFooterStart>
      <SectionFooterEnd>
        {endActions && (
          <ControlGroup size="small" relation="separate">
            {endActions}
          </ControlGroup>
        )}
      </SectionFooterEnd>
    </SectionFooterContainer>
  );
}

const SectionDivider = styled(Divider, {
  variants: {
    relation: {
      stacked: {},
      standalone: {},
    },
    siblingAware: {
      true: {},
      false: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'x-small',
      css: {
        padding: sizing.contentSidesOnly,
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'small',
      css: {
        padding: sizing.contentSidesOnly,
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'medium',
      css: {
        padding: sizing.sidesOnly,
      },
    },
  ],
});
SectionDivider.displayName = 'SectionDivider';

const SectionDividerTop = styled(SectionDivider, {
  maxHeight: 0, // Forces a unique CSS selector
});
SectionDividerTop.displayName = 'SectionDividerTop';

const SectionDividerBottom = styled(SectionDivider, {
  minHeight: 0, // Forces a unique CSS selector
});
SectionDividerBottom.displayName = 'SectionDividerBottom';

const SectionInner = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',

  variants: {
    siblingAware: {
      true: {},
      false: {},
    },
    bordersAlways: {
      true: {},
      false: {},
    },
    relation: {
      stacked: {},
      standalone: {},
    },
    size: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'x-small',
      css: {
        '& > *:first-child': {
          marginTop: '$8',
        },

        '& > *:last-child': {
          marginBottom: '$8',
        },
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'small',
      css: {
        '& > *:first-child': {
          marginTop: '$8',
        },

        '& > *:last-child': {
          marginBottom: '$8',
        },
      },
    },
    {
      relation: 'stacked',
      siblingAware: true,
      size: 'medium',
      css: {
        '& > *:first-child': {
          marginTop: '$12',
        },

        '& > *:last-child': {
          marginBottom: '$12',
        },
      },
    },
  ],
});
SectionInner.displayName = 'SectionInner';

const SectionContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  width: '100%',

  [`&:first-child ${SectionDividerTop}`]: {
    display: 'none',
  },

  [`&:last-child ${SectionDividerBottom}`]: {
    display: 'none',
  },

  variants: {
    relation: {
      stacked: {},
      standalone: {
        strokeAll: colors.strokeNeutralLight,
        borderRadius: '$8',

        [darkThemeSelector]: {
          strokeAll: colors.strokeNeutralDark,
        },
      },
    },
    siblingAware: {
      true: {},
      false: {},
    },
    bordersAlways: {
      true: {
        [`&:first-child ${SectionDividerTop}`]: {
          display: 'flex',
        },

        [`&:last-child ${SectionDividerBottom}`]: {
          display: 'flex',
        },
      },
      false: {},
    },
  },
});
SectionContainer.displayName = 'SectionContainer';

export type SectionIDProp = string;
export type SectionPropRelation = 'stacked' | 'standalone';
export type SectionPropSize = 'x-small' | 'small' | 'medium';

export type SectionProps = {
  /**
   * Access level for the section.
   */
  accessLevel?: AccessLevelProp;
  /**
   * Boolean to show borders always.
   */
  bordersAlways?: boolean;
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Boolean to collapse the section.
   */
  collapsible?: boolean;
  /**
   * Apply a gradient fade to the edges of scrollable section content. Defaults to `'none'`.
   * Fade is only applied on {@link SectionContent} that has `overflow="auto"` or `overflow="scroll"`.
   */
  fade?: SectionFadeProp;
  /**
   * The ID of the section.
   */
  id?: SectionIDProp;
  /**
   * Boolean to show internal-only styles.
   */
  internal?: InternalProp;
  /**
   * Boolean to show a divider between sections.
   */
  siblingAware?: boolean;
  /**
   * Determines how the section is related to its siblings.
   */
  relation?: SectionPropRelation;
  /**
   * The size of the section.
   */
  size?: SectionPropSize;
  /**
   * The appearance of the divider for sibling aware sections.
   */
  dividerAppearance?: DividerAppearance;
} & ComponentProps<typeof SectionContainer>;

export function Section({
  accessLevel: accessLevelProp,
  children,
  collapsible = false,
  bordersAlways = false,
  fade = 'none',
  id = '',
  internal,
  relation,
  siblingAware = true,
  size = 'medium',
  dividerAppearance,
  ...remaining
}: SectionProps) {
  const sectionContext = useSectionContext();
  const isParentUsingDefaultSize = sectionContext.size === 'medium';
  const effectiveRelation = relation ?? sectionContext.relation ?? 'standalone';
  const effectiveSize = isParentUsingDefaultSize ? size : sectionContext.size ?? size;
  const isStacked = effectiveRelation === 'stacked';
  const hasDivider = isStacked && (bordersAlways || siblingAware);
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });

  return (
    <SectionContainer relation={effectiveRelation} bordersAlways={bordersAlways} {...remaining}>
      {hasDivider && (
        <SectionDividerTop
          direction="row"
          relation={effectiveRelation}
          siblingAware={siblingAware}
          size={effectiveSize}
          appearance={dividerAppearance}
        />
      )}
      <SectionInner relation={effectiveRelation} siblingAware={siblingAware} size={effectiveSize}>
        <SectionProvider
          id={id}
          collapsible={collapsible}
          relation={effectiveRelation}
          size={effectiveSize}
          fade={fade}
        >
          <UserAccessLevelContextProvider value={accessLevel}>
            {children}
          </UserAccessLevelContextProvider>
        </SectionProvider>
      </SectionInner>
      {hasDivider && (
        <SectionDividerBottom
          direction="row"
          relation={effectiveRelation}
          siblingAware={siblingAware}
          size={effectiveSize}
          appearance={dividerAppearance}
        />
      )}
    </SectionContainer>
  );
}

const SectionsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
SectionsContainer.displayName = 'SectionsContainer';

type SectionsProps = {
  children: React.ReactNode;
  gap?: SpacingScale;
  relation?: SectionPropRelation;
  size?: SectionPropSize;
} & GutterProp &
  ComponentProps<typeof SectionsContainer>;

export function Sections({
  children,
  gap = 0,
  gutter = { edge: 'none' },
  relation = 'stacked',
  size = 'medium',
  ...remaining
}: SectionsProps) {
  const effectiveGutterContext = relation === 'standalone' ? 'application' : 'section';
  const effectiveGutterEdge = relation === 'standalone' ? 'all' : gutter.edge;
  const effectiveGap = relation === 'standalone' ? 20 : gap;

  return (
    <SectionsContainer
      css={merge(
        gutterCSS({
          context: effectiveGutterContext,
          edge: effectiveGutterEdge,
        }),
        spacingCSS(effectiveGap),
      )}
      {...remaining}
    >
      <SectionProvider relation={relation} size={size}>
        {children}
      </SectionProvider>
    </SectionsContainer>
  );
}
